import React from 'react';
import { Card, CardBody } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { FileSearch, Search, FileText, Key, Pencil, BookOpen } from 'lucide-react';

const PdfConversionGuide: React.FC = () => {
  const { t } = useTranslation('admin');

  return (
    <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800">
      <CardBody className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
            <BookOpen className="w-6 h-6 text-green-700 dark:text-green-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {t('guides.modal.pdfImport.guide.title', { defaultValue: 'PDF to Tutorial Conversion' })}
          </h3>
        </div>
        <ul className="space-y-3">
          <li className="flex items-start gap-3">
            <div className="p-2 bg-white dark:bg-gray-800 rounded-lg flex-shrink-0">
              <FileSearch className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300 flex-1">
              <strong className="text-blue-700 dark:text-blue-300">
                {t('guides.modal.pdfImport.guide.step1.title', {
                  defaultValue: 'Extract text and structure from your PDF:',
                })}
              </strong>{' '}
              {t('guides.modal.pdfImport.guide.step1.description', {
                defaultValue:
                  'Our system scans your PDF document to extract all text content and understand its structure.',
              })}
            </p>
          </li>
          <li className="flex items-start gap-3">
            <div className="p-2 bg-white dark:bg-gray-800 rounded-lg flex-shrink-0">
              <Search className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300 flex-1">
              <strong className="text-blue-700 dark:text-blue-300">
                {t('guides.modal.pdfImport.guide.step2.title', {
                  defaultValue: 'Analyze content and identify key concepts:',
                })}
              </strong>{' '}
              {t('guides.modal.pdfImport.guide.step2.description', {
                defaultValue:
                  'AI analyzes the extracted content to identify important concepts, topics, and learning objectives.',
              })}
            </p>
          </li>
          <li className="flex items-start gap-3">
            <div className="p-2 bg-white dark:bg-gray-800 rounded-lg flex-shrink-0">
              <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300 flex-1">
              <strong className="text-blue-700 dark:text-blue-300">
                {t('guides.modal.pdfImport.guide.step3.title', {
                  defaultValue: 'Generate organized tutorial steps:',
                })}
              </strong>{' '}
              {t('guides.modal.pdfImport.guide.step3.description', {
                defaultValue:
                  'The content is automatically organized into clear, sequential tutorial steps for easy learning.',
              })}
            </p>
          </li>
          <li className="flex items-start gap-3">
            <div className="p-2 bg-white dark:bg-gray-800 rounded-lg flex-shrink-0">
              <Key className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300 flex-1">
              <strong className="text-blue-700 dark:text-blue-300">
                {t('guides.modal.pdfImport.guide.step4.title', {
                  defaultValue: 'Extract technical terms for glossary:',
                })}
              </strong>{' '}
              {t('guides.modal.pdfImport.guide.step4.description', {
                defaultValue:
                  'Important technical terms and definitions are automatically identified and prepared for your glossary.',
              })}
            </p>
          </li>
          <li className="flex items-start gap-3">
            <div className="p-2 bg-white dark:bg-gray-800 rounded-lg flex-shrink-0">
              <Pencil className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300 flex-1">
              <strong className="text-blue-700 dark:text-blue-300">
                {t('guides.modal.pdfImport.guide.step5.title', {
                  defaultValue: 'You can edit and refine the generated content:',
                })}
              </strong>{' '}
              {t('guides.modal.pdfImport.guide.step5.description', {
                defaultValue:
                  'After generation, you have full control to edit, refine, and customize the tutorial to match your needs.',
              })}
            </p>
          </li>
        </ul>
      </CardBody>
    </Card>
  );
};

PdfConversionGuide.displayName = 'PdfConversionGuide';

export default PdfConversionGuide;

